/**
 * navigation.ts — sentral navigasjons-config med rollebasert tilgang
 *
 * Definerer alle meny-elementer for header-nav og side-nav.
 * Hvert element har `role_ids` som bestemmer hvilke role_id-er som ser det.
 *
 * NB: Vi bruker role_id (number) fra backend — IKKE strenger som 'admin'/'editor'.
 * Backend er kilde til sannhet for hva role_id betyr (se API.md).
 * Per nåværende API.md: 1 = guest, 2 = user, 3 = admin.
 *
 * UiShell filtrerer basert på innlogget brukers role_id via hasRole().
 *
 * Struktur:
 * 1. headerNav — top bar lenker
 * 2. sideNav — sidemeny med lenker og utvidbare menyer
 */

export interface NavItem {
  href: string;
  label: string;
  role_ids: string[];
}

export interface NavMenu {
  title: string;
  role_ids: string[];
  children: NavItem[];
}

export type SideNavItem = NavItem | NavMenu;

// Type guard: sjekker om et element er en meny (har children)
export function isNavMenu(item: SideNavItem): item is NavMenu {
  return 'children' in item;
}

// Rolle-ID-er per API.md // DETTE ER DET ENESTE UNNTAKET AV TOLKNING AV APIET
const ROLE_ID_ADMIN = 'f7ae90d2-1432-4d94-b7f5-5739ead57f3f';
const ROLE_ID_USER = '1';
const ROLE_ID_GUEST = '1';
const ALL_ROLES = [ROLE_ID_ADMIN, ROLE_ID_USER, ROLE_ID_GUEST];

// === 1. Header-nav (top bar) ===
export const headerNav: NavItem[] = [
  {
    href: '/dashboard',
    label: 'Dashboard',
    role_ids: [ROLE_ID_ADMIN],
  },
  {
    href: '/showcase',
    label: 'Komponenter',
    role_ids: [ROLE_ID_ADMIN],
  }, 
  {
    href: '/docs',
    label: 'Dokumentasjon',
    role_ids: [ROLE_ID_ADMIN],
  },
];

// === 2. Side-nav (sidemeny) ===
export const sideNav: SideNavItem[] = [
  {
    href: '/dashboard',
    label: 'inAdmin Dashboard',
    role_ids: [ROLE_ID_ADMIN],
  },
  {
    title: 'inFiles',
    role_ids: [ROLE_ID_ADMIN, ROLE_ID_USER],
    children: [
      {
        href: '/jobs',
        label: 'Jobs',
        role_ids: [ROLE_ID_ADMIN, ROLE_ID_USER],
      },
      {
        href: '/types',
        label: 'Types',
        role_ids: [ROLE_ID_ADMIN, ROLE_ID_USER],
      },
      {
        href: '/kjoretoy',
        label: 'Kjøretøy',
        role_ids: [ROLE_ID_ADMIN, ROLE_ID_USER],
      },
      {
        href: '/forhandlere',
        label: 'Forhandlere',
        role_ids: [ROLE_ID_ADMIN],
      },
    ],
  },
  {
    title: 'InRental',
    role_ids: [ROLE_ID_ADMIN, ROLE_ID_USER],
    children: [
      {
        href: '/in-rental',
        label: 'InRental',
        role_ids: [ROLE_ID_ADMIN, ROLE_ID_USER],
      },
      {
        href: '/in-rental/booking-calendar',
        label: 'Booking Calendar',
        role_ids: [ROLE_ID_ADMIN, ROLE_ID_USER],
      },
      {
        href: '/in-rental/booking-list',
        label: 'Booking List',
        role_ids: [ROLE_ID_ADMIN, ROLE_ID_USER],
      },
    ],
  },
  {
    title: 'inAds',
    role_ids: [ROLE_ID_ADMIN, ROLE_ID_USER],
    children: [
      {
        href: '/in-ads/dashboard',
        label: 'Dashboard',
        role_ids: [ROLE_ID_ADMIN, ROLE_ID_USER],
      },
    ],
  },
  {
    title: 'CRM',
    role_ids: ALL_ROLES,
    children: [
      {
        href: '/crm/company',
        label: 'Company',
        role_ids: [ROLE_ID_ADMIN],
      },
      {
        href: '/crm/persons',
        label: 'Persons',
        role_ids: [ROLE_ID_ADMIN],
      },
      {
        href: '/crm/customers',
        label: 'Customers',
        role_ids: [ROLE_ID_ADMIN],
      },
    ],
  },
    {
    title: 'Administrasjon',
    role_ids: [ROLE_ID_ADMIN],
    children: [
      {
        href: '/users',
        label: 'Brukere',
        role_ids: [ROLE_ID_ADMIN],
      },
      {
        href: '/tenants',
        label: 'Tenants',
        role_ids: [ROLE_ID_ADMIN],
      },
      {
        href: '/roles',
        label: 'Roller',
        role_ids: [ROLE_ID_ADMIN],
      },
    ],
  },
  {
    href: '/logout',
    label: 'Logg ut',
    role_ids: ALL_ROLES,
  },
];
